import '../broiler/poultryDesign.css';
import Segmented from '../shared/Segmented.js';
import {StatStrip} from '../shared/StatStrip.js';
// ============================================================================
// src/layer/LayersHomeView.tsx  —  Phase 2 Round 6
// ----------------------------------------------------------------------------
// Layers home dashboard — per-batch lifetime feed/egg/cost stats for active batches.
//
// B3.3: it owns its five reads through layer.home.core. It used to render
// entirely from providers App's boot filled — LayerContext for the two layer
// directories and the two complete daily histories, FeedCostsContext for the
// per-lb fallback rates — with no physical read of its own, which is what put
// `layersHome` in the frozen app_store / layer_batches / layer_housings
// consumer lists. It reads none of those now.
//
// useLayer STAYS, for two values and neither is data: `retHomeDashPeriod` and
// its setter are a `useState(30)` pair living in LayerContext, not something
// App's boot ever writes. They are the Retirement Home card's rolling-window
// toggle, and moving them into this component would reset the operator's choice
// on every navigation away and back — a behaviour change under cover of a
// refactor. There is no residual-legacy id for them because they are not a
// legacy boot dependency; see src/app/data/migratedSurfaces/view.layersHome.ts.
// ============================================================================
import React from 'react';
import {useAppNavigate} from '../app/navigation/index.js';
import {openableProps} from '../shared/openable.js';
import {getProgramColor} from '../lib/programColors.js';

import Badge from '../shared/Badge.jsx';
import {GroupDot} from '../shared/GroupPillPicker.js';
import {buildLayerGroupPalette, layerGroupColor, layerGroupTint} from './layerGroupPalette.js';
import {layerGroupStatusLabel, resolveLayerGroupStatus} from '../lib/layerGroupStatus.js';
import type {LayerGroupsPayload} from '../lib/layerGroupStatus.js';
import {fmtDate as fmt, fmtDate as fmtS, todayISO, addDays, toISO} from '../lib/dateUtils.js';
import {S} from '../lib/styles.js';
import {computeHousingDisplayCount, computeLayerFeedCost} from '../lib/layerHousing.js';
import {useLayer} from '../contexts/LayerContext.jsx';
import {useUI} from '../contexts/UIContext.jsx';
import {RESOURCE} from '../app/data/routeDataCatalog.js';
import type {LayersHomeCoreData} from '../app/data/routeDataCatalog.js';
import {useRouteResource} from '../app/data/useRouteResource.js';
import Header from '../shared/Header.tsx';
import type {ReactNode} from 'react';

// A layer_batches row, reduced to the columns this dashboard reads.
//
// `name` is the one NON-optional field, and that is read off this file rather
// than assumed: computeBatchWindow below does `batch.name.toLowerCase()` with no
// guard, so the view already requires it. LayerBatchesView.saveNewBatch is the
// only creator of these rows and refuses an empty trimmed name.
//
// The three per-lb rates are the batch's frozen cost snapshot. They are
// `number | string | null` because computeLayerFeedCost (src/lib/layerHousing.js)
// parses every one of them through parseFloat, which is what a column that can
// hold either a numeric or a webform-written text value needs.
type LayerBatchRecord = {
  id?: string | null;
  name: string;
  status?: string | null;
  brooder_entry_date?: string | null;
  arrival_date?: string | null;
  per_lb_starter_cost?: number | string | null;
  per_lb_grower_cost?: number | string | null;
  per_lb_layer_cost?: number | string | null;
};

// A layer_housings row - only the four columns read here.
type LayerHousingRecord = {
  id?: string | null;
  batch_id?: string | null;
  housing_name?: string | null;
  status?: string | null;
};

// One layer_dailys row. `date` is NOT optional for the same reason `name` is
// not above: every read of it in this file is either an unguarded relational
// window test (`d.date < fromISO`) or a `new Date(d.date + 'T12:00:00')`, so the
// dashboard already treats a report as always carrying its report date - which
// is also the column migration 084 keys the active-daily unique index on.
// feed_lbs / mortality_count are `number | string` because a report reaches this
// table from the app (numeric) and from the webform (text).
type LayerDailyRecord = {
  date: string;
  batch_id?: string | null;
  batch_label?: string | null;
  feed_lbs?: number | string | null;
  mortality_count?: number | string | null;
  feed_type?: string | null;
};

// One egg_dailys row: up to four (group name, count) pairs plus the on-hand
// figure the top stat tile shows. Same `date` reasoning as above.
type EggDailyRecord = {
  date: string;
  group1_name?: string | null;
  group1_count?: number | string | null;
  group2_name?: string | null;
  group2_count?: number | string | null;
  group3_name?: string | null;
  group3_count?: number | string | null;
  group4_name?: string | null;
  group4_count?: number | string | null;
  dozens_on_hand?: number | string | null;
};

// What computeBatchWindow and computeHousingWindow both return. `days` is
// optional because only computeBatchWindow computes it - computeHousingWindow's
// return literal has no such key, and only the batch cards ask for it via
// showDaysDenominator.
type LayerWindowStats = {
  totalFeed: number;
  starterFeed: number;
  growerFeed: number;
  layerFeed: number;
  totalMort: number;
  totalEggs: number;
  dozens: number;
  cost: number | null;
  hens: number;
  epd: number | null;
  costPerDoz: number | null;
  reportDays: number;
  days?: number;
};

// One cell of the metrics grid. Named rather than inferred: the literals below
// carry `good`, `warn` and `trend` on some entries and not others, so an
// inferred element type would be a union with no common shape and the three
// reads in the renderer would not resolve against it.
type MetricItem = {
  l: string;
  v: string | number;
  good?: boolean;
  warn?: boolean;
  trend?: ReactNode;
};

// Frozen module constants, not inline literals. `LAYERS_HOME_PARAMS` because
// useRouteResource serializes params into its key and a fresh {} every render
// would rebuild the key forever; the two empties because a new [] every render
// would give every `(allLayerDailys || [])` guard below a fresh identity and
// defeat memoization the same way.
const LAYERS_HOME_PARAMS = Object.freeze({});
const EMPTY_ROWS: readonly never[] = Object.freeze([]);
const EMPTY_FEED_COSTS = Object.freeze({starter: 0, grower: 0, layer: 0, pig: 0, grit: 0});

// R08: a card is radius 18 on --shadow-card; the section title above a group of
// cards is 18/800, the same heading the broiler, cattle and sheep dashboards use.
const cardS: React.CSSProperties = {
  background: 'var(--surface)',
  border: '1px solid var(--border)',
  borderRadius: 'var(--r-card)',
  boxShadow: 'var(--shadow-card)',
};
const sectionTitleS: React.CSSProperties = {
  margin: 0,
  fontSize: 18,
  fontWeight: 800,
  letterSpacing: '-.01em',
  color: 'var(--ink)',
};
const cardTitleS: React.CSSProperties = {fontSize: 16, fontWeight: 800, color: 'var(--ink)'};
const cardMetaS: React.CSSProperties = {fontSize: 13, fontWeight: 600, color: 'var(--text-2)'};

export default function LayersHomeView() {
  const navigate = useAppNavigate();
  // The rolling-window toggle only. Every DATA field this hook used to hand
  // over is read below through layer.home.core; see the file header for why
  // these two stay.
  const {retHomeDashPeriod, setRetHomeDashPeriod} = useLayer();
  const homeResource = useRouteResource(RESOURCE.LAYERS_HOME_CORE, LAYERS_HOME_PARAMS);
  const home = homeResource.data as LayersHomeCoreData | null;
  // The one place this view states what it believes its four opaque row arrays
  // hold. Asserting once here, rather than at each of the ~60 field reads
  // below, keeps the claim reviewable and keeps the reads unannotated. The
  // arrays are `{[column: string]: unknown}[]` from the catalog for exactly the
  // reason they were `unknown[]` from LayerContext: the reads are `select('*')`
  // and no generated database types exist.
  //
  // The `|| EMPTY` fallbacks are what an unresolved resource renders as, and
  // they are the same empty arrays a degraded boot published — so the first
  // paint of this dashboard is the paint it has always had while the boot
  // sidecar was still in flight.
  const layerBatches = (home?.layerBatches || EMPTY_ROWS) as LayerBatchRecord[];
  // The canonical groups this surface already declares (layer.eggDailys.groups,
  // a permanent obligation): the Retirement Home status AND the R06 group
  // palette both read it, so it loads for normal groups too.
  const groupResource = useRouteResource(RESOURCE.EGG_DAILYS_GROUPS, LAYERS_HOME_PARAMS);
  const retirementHomeStatus = resolveLayerGroupStatus({
    data: groupResource.data as LayerGroupsPayload | null,
    error: groupResource.error,
  }).index.get('retirement home');
  // R06: group colors by canonical order, from that same canonical read. While
  // it is pending, failed or denied, no group carries a dot rather than a
  // guessed one.
  const groupPalette = buildLayerGroupPalette((groupResource.data as LayerGroupsPayload | null)?.layerGroups);
  const layerHousings = (home?.layerHousings || EMPTY_ROWS) as LayerHousingRecord[];
  const allLayerDailys = (home?.allLayerDailys || EMPTY_ROWS) as LayerDailyRecord[];
  const allEggDailys = (home?.allEggDailys || EMPTY_ROWS) as EggDailyRecord[];
  const feedCosts = home?.feedCosts || EMPTY_FEED_COSTS;
  const {setView} = useUI();
  const fmt$ = (v: number | null | undefined) =>
    v == null ? '\u2014' : '$' + v.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2});
  const fmt$0 = (v: number | null | undefined) =>
    v == null ? '\u2014' : '$' + v.toLocaleString(undefined, {minimumFractionDigits: 0, maximumFractionDigits: 0});
  const EMPTY_METRIC_LABEL = 'No data yet';
  const lifetimeFromForBatch = (batch: LayerBatchRecord) =>
    batch.brooder_entry_date || batch.arrival_date || '1900-01-01';

  // Compute stats for a given batch over an arbitrary date window.
  // Attribution rule: a report belongs to this batch if
  //   (a) its batch_id matches (most reliable for new submissions), OR
  //   (b) its batch_label matches the batch name OR any of this batch's housing names,
  //       AND no other active batch claims that name (text fallback for legacy rows).
  function computeBatchWindow(batch: LayerBatchRecord, fromISO: string, toISO: string): LayerWindowStats {
    const anchor = batch.brooder_entry_date || batch.arrival_date || null;
    const myHousings = (layerHousings || []).filter((h) => h.batch_id === batch.id);
    const myNames = new Set([
      batch.name.toLowerCase().trim(),
      ...myHousings.map((h) =>
        String(h.housing_name || '')
          .toLowerCase()
          .trim(),
      ),
    ]);
    const myReports = (allLayerDailys || []).filter((d) => {
      if (d.date < fromISO || d.date > toISO) return false;
      if (d.batch_id === batch.id) return true;
      if (d.batch_id) return false; // batch_id set but doesn't match — skip
      return myNames.has(
        String(d.batch_label || '')
          .toLowerCase()
          .trim(),
      );
    });
    let totalFeed = 0,
      totalMort = 0,
      starterFeed = 0,
      growerFeed = 0,
      layerFeed = 0;
    const reportDates = new Set();
    myReports.forEach((d) => {
      // String() around columns that are already numeric on app-written rows:
      // parseFloat/parseInt are DECLARED to take a string and did the same
      // ToString conversion themselves.
      const f = parseFloat(String(d.feed_lbs)) || 0;
      totalFeed += f;
      totalMort += parseInt(String(d.mortality_count)) || 0;
      reportDates.add(d.date);
      let phase = d.feed_type || 'LAYER';
      if (anchor) {
        try {
          // `.getTime()` is what the `-` operator already did: subtracting two
          // Dates coerces each through valueOf(), which returns exactly this
          // number.
          const days = Math.floor(
            (new Date(d.date + 'T12:00:00').getTime() - new Date(anchor + 'T12:00:00').getTime()) / 86400000,
          );
          if (days < 21) phase = 'STARTER';
          else if (days < 140) phase = 'GROWER';
          else phase = 'LAYER';
        } catch (_e) {
          /* defensive parse — fall through to default phase */
        }
      }
      if (phase === 'STARTER') starterFeed += f;
      else if (phase === 'GROWER') growerFeed += f;
      else layerFeed += f;
    });
    const hNames = new Set(
      myHousings.map((h) =>
        String(h.housing_name || '')
          .toLowerCase()
          .trim(),
      ),
    );
    let totalEggs = 0;
    (allEggDailys || [])
      .filter((d) => d.date >= fromISO && d.date <= toISO)
      .forEach((d) => {
        [
          [d.group1_name, d.group1_count],
          [d.group2_name, d.group2_count],
          [d.group3_name, d.group3_count],
          [d.group4_name, d.group4_count],
        ].forEach(([n, ct]) => {
          // Same String() note as above — parseInt already did this conversion.
          if (n && hNames.has(String(n).toLowerCase().trim())) totalEggs += parseInt(String(ct)) || 0;
        });
      });
    const dozens = totalEggs / 12;
    // If batch has no rates (e.g. Retirement Home before first feed cost save), fall back to global rates
    var costBatch = batch;
    if (
      !parseFloat(String(batch.per_lb_starter_cost)) &&
      !parseFloat(String(batch.per_lb_grower_cost)) &&
      !parseFloat(String(batch.per_lb_layer_cost))
    ) {
      costBatch = {
        ...batch,
        per_lb_starter_cost: feedCosts.starter || 0,
        per_lb_grower_cost: feedCosts.grower || 0,
        per_lb_layer_cost: feedCosts.layer || 0,
      };
    }
    const cost = computeLayerFeedCost(starterFeed, growerFeed, layerFeed, costBatch);
    const hens = myHousings
      .filter((h) => h.status === 'active')
      .reduce((s, h) => s + computeHousingDisplayCount(h, allLayerDailys, layerHousings), 0);
    // Eggs/hen/day uses span of window
    // `.getTime()` as above: the `-` operator already coerced both Dates through
    // valueOf(), which returns exactly this number.
    const days = Math.max(
      1,
      Math.round((new Date(toISO + 'T12:00:00').getTime() - new Date(fromISO + 'T12:00:00').getTime()) / 86400000) + 1,
    );
    const epd = hens > 0 && totalEggs > 0 ? totalEggs / (hens * days) : null;
    const costPerDoz = dozens > 0 && cost != null ? cost / dozens : null;
    return {
      totalFeed,
      starterFeed,
      growerFeed,
      layerFeed,
      totalMort,
      totalEggs,
      dozens,
      cost,
      hens,
      epd,
      costPerDoz,
      reportDays: reportDates.size,
      days,
    };
  }

  // Compute housing stats over a window
  function computeHousingWindow(
    housing: LayerHousingRecord,
    batch: LayerBatchRecord | null,
    fromISO: string,
    toISO: string,
  ): LayerWindowStats {
    const anchor = batch ? batch.brooder_entry_date || batch.arrival_date || null : null;
    const hName = String(housing.housing_name || '')
      .toLowerCase()
      .trim();
    const myReports = (allLayerDailys || []).filter((d) => {
      if (d.date < fromISO || d.date > toISO) return false;
      if (
        String(d.batch_label || '')
          .toLowerCase()
          .trim() !== hName
      )
        return false;
      // If batch_id is set on the report, it must match this housing's parent batch
      if (d.batch_id && batch && d.batch_id !== batch.id) return false;
      return true;
    });
    let totalFeed = 0,
      totalMort = 0,
      starterFeed = 0,
      growerFeed = 0,
      layerFeed = 0;
    const reportDates = new Set();
    myReports.forEach((d) => {
      // String() around columns that are already numeric on app-written rows:
      // parseFloat/parseInt are DECLARED to take a string and did the same
      // ToString conversion themselves.
      const f = parseFloat(String(d.feed_lbs)) || 0;
      totalFeed += f;
      totalMort += parseInt(String(d.mortality_count)) || 0;
      reportDates.add(d.date);
      let phase = d.feed_type || 'LAYER';
      if (anchor) {
        try {
          // `.getTime()` is what the `-` operator already did: subtracting two
          // Dates coerces each through valueOf(), which returns exactly this
          // number.
          const days = Math.floor(
            (new Date(d.date + 'T12:00:00').getTime() - new Date(anchor + 'T12:00:00').getTime()) / 86400000,
          );
          if (days < 21) phase = 'STARTER';
          else if (days < 140) phase = 'GROWER';
          else phase = 'LAYER';
        } catch (_e) {
          /* defensive parse — fall through to default phase */
        }
      }
      if (phase === 'STARTER') starterFeed += f;
      else if (phase === 'GROWER') growerFeed += f;
      else layerFeed += f;
    });
    let totalEggs = 0;
    (allEggDailys || [])
      .filter((d) => d.date >= fromISO && d.date <= toISO)
      .forEach((d) => {
        [
          [d.group1_name, d.group1_count],
          [d.group2_name, d.group2_count],
          [d.group3_name, d.group3_count],
          [d.group4_name, d.group4_count],
        ].forEach(([n, ct]) => {
          // Same String() note as above — parseInt already did this conversion.
          if (n && String(n).toLowerCase().trim() === hName) totalEggs += parseInt(String(ct)) || 0;
        });
      });
    const dozens = totalEggs / 12;
    var costBatchH = batch;
    if (
      batch &&
      !parseFloat(String(batch.per_lb_starter_cost)) &&
      !parseFloat(String(batch.per_lb_grower_cost)) &&
      !parseFloat(String(batch.per_lb_layer_cost))
    ) {
      costBatchH = {
        ...batch,
        per_lb_starter_cost: feedCosts.starter || 0,
        per_lb_grower_cost: feedCosts.grower || 0,
        per_lb_layer_cost: feedCosts.layer || 0,
      };
    }
    const cost = costBatchH ? computeLayerFeedCost(starterFeed, growerFeed, layerFeed, costBatchH) : null;
    const hens = computeHousingDisplayCount(housing, allLayerDailys, layerHousings);
    // `.getTime()` as above: the `-` operator already coerced both Dates through
    // valueOf(), which returns exactly this number.
    const days = Math.max(
      1,
      Math.round((new Date(toISO + 'T12:00:00').getTime() - new Date(fromISO + 'T12:00:00').getTime()) / 86400000) + 1,
    );
    const epd = hens > 0 && totalEggs > 0 ? totalEggs / (hens * days) : null;
    const costPerDoz = dozens > 0 && cost != null ? cost / dozens : null;
    return {
      totalFeed,
      starterFeed,
      growerFeed,
      layerFeed,
      totalMort,
      totalEggs,
      dozens,
      cost,
      hens,
      epd,
      costPerDoz,
      reportDays: reportDates.size,
    };
  }

  // Period dates
  const today = todayISO();

  // Retirement Home period
  const retFrom = toISO(addDays(new Date(), -(retHomeDashPeriod - 1)));

  // Trend renderer — suppresses noise from near-zero baselines
  const trendArrow = (cur: number | null | undefined, prev: number | null | undefined, higherIsBetter: boolean) => {
    if (cur == null || prev == null || prev === 0) return null;
    // If the previous value is negligible relative to current, the % is meaningless
    if (Math.abs(prev) < 0.05) return null;
    const pct = ((cur - prev) / prev) * 100;
    if (Math.abs(pct) > 200) return null; // cap noise from tiny baselines
    const up = pct >= 0;
    const good = higherIsBetter ? up : !up;
    const color = Math.abs(pct) < 2 ? 'var(--text-secondary)' : good ? 'var(--ok-ink)' : 'var(--danger)';
    const arrow = Math.abs(pct) < 2 ? '\u2192' : up ? '\u2191' : '\u2193';
    return (
      <span style={{fontSize: 10, color, fontWeight: 600, marginLeft: 4}}>
        {arrow} {Math.abs(pct).toFixed(0)}%
      </span>
    );
  };

  // Build batch list — exclude Retirement Home, only active
  const dashBatches = (layerBatches || []).filter((b) => b.status === 'active' && b.name !== 'Retirement Home');
  const retHome = (layerBatches || []).find((b) => b.name === 'Retirement Home');

  // Top stats
  const activeHousings = (layerHousings || []).filter((h) => h.status === 'active');
  const totalHens = activeHousings.reduce((s, h) => {
    return s + computeHousingDisplayCount(h, allLayerDailys, layerHousings);
  }, 0);
  const last7iso = toISO(addDays(new Date(), -7));
  const totalEggsLast7 = (allEggDailys || [])
    .filter((d) => d.date >= last7iso)
    .reduce(
      // String() around counts that are already numeric on app-written rows:
      // parseInt is DECLARED to take a string and did the same ToString
      // conversion itself.
      (s, d) =>
        s +
        (parseInt(String(d.group1_count)) || 0) +
        (parseInt(String(d.group2_count)) || 0) +
        (parseInt(String(d.group3_count)) || 0) +
        (parseInt(String(d.group4_count)) || 0),
      0,
    );
  const latestEggReport = (allEggDailys || [])[0];
  const dozensOnHand = latestEggReport?.dozens_on_hand || 0;

  // Summary cards keep four facts; detailed feed and production measures live on the record.
  const MetricsGrid = ({s, showDaysDenominator = false}: {s: LayerWindowStats; showDaysDenominator?: boolean}) => {
    const hasDashboardData =
      s.reportDays > 0 || s.totalFeed > 0 || s.totalEggs > 0 || s.totalMort > 0 || s.epd != null || s.hens > 0;
    if (!hasDashboardData)
      return (
        <div data-layer-dashboard-empty="metrics" className="poultry-meta">
          {EMPTY_METRIC_LABEL}
        </div>
      );
    return (
      <>
        <dl className="poultry-fact-rows">
          {[
            {label: 'Hens', value: s.hens > 0 ? s.hens.toLocaleString() : '—'},
            {label: 'Mortality', value: s.totalMort.toLocaleString(), warn: s.totalMort > 10},
            {label: 'Total Feed', value: s.totalFeed > 0 ? Math.round(s.totalFeed).toLocaleString() + ' lb' : '—'},
            {label: 'Dozens', value: s.dozens > 0 ? Math.floor(s.dozens).toLocaleString() : '—'},
          ].map(({label, value, warn}) => (
            <div key={label}>
              <dt>{label}</dt>
              <dd style={warn ? {color: 'var(--danger-ink)'} : undefined}>{value}</dd>
            </div>
          ))}
        </dl>
        <div className="poultry-meta">
          {showDaysDenominator && s.days ? `${s.reportDays} of ${s.days}` : s.reportDays || 0} report days
        </div>
      </>
    );
  };

  // The one call site passes `retHomeDashPeriod` / `setRetHomeDashPeriod`
  // (LayerContext's `useState(30)` pair) and a literal option list, so the
  // window value is a number and `setVal` is the plain one-argument call this
  // component makes of the state setter.
  const PeriodToggle = ({
    val,
    setVal,
    opts,
  }: {
    val: number;
    setVal: (next: number) => void;
    opts: {v: number; l: string}[];
  }) => (
    <Segmented
      aria-label="Report Period"
      value={String(val)}
      onChange={(value) => setVal(Number(value))}
      options={opts.map(({v, l}) => ({value: String(v), label: l}))}
    />
  );

  // The boot-release readiness marker. THIS VIEW HAS ONE RETURN AND NO LOADING
  // OR ERROR BRANCH, and that is deliberate rather than an omission — see the
  // file header and view.layersHome's declaration: every input degrades to an
  // empty array, so the first paint while layer.home.core is in flight is the
  // paint this dashboard has always had. The attribute is therefore the only
  // way to tell "painting empties" from "painting the farm", which is exactly
  // what an independent boot proof has to wait on. Same expression shape as
  // CattleHomeView.tsx and SheepHomeView.tsx; it changes no render output.
  return (
    <div
      style={{minHeight: '100vh', background: 'var(--bg-page)', overflow: 'hidden'}}
      data-layer-home-loaded={homeResource.loading || Boolean(homeResource.error) ? 'false' : 'true'}
    >
      <Header />
      <div
        style={{
          padding: '1.25rem',
          maxWidth: 1180,
          margin: '0 auto',
          display: 'flex',
          flexDirection: 'column',
          gap: '1.25rem',
          minWidth: 0,
        }}
      >
        <h1 className="poultry-page-title">Layers</h1>
        {/* R15: one joined stat strip, not four loose tiles. The values are
            the same four expressions; an empty on-hand figure is the strip's
            muted dash (R14). The one sub-line is the report date the on-hand
            count is read from (R11). */}
        <StatStrip
          rootAttrs={{'data-layer-home-strip': '1'}}
          tiles={[
            {label: 'Active Flocks', value: activeHousings.length},
            {label: 'Total Hens', value: totalHens.toLocaleString()},
            {label: 'Dozens Last 7 Days', value: Math.floor(totalEggsLast7 / 12).toLocaleString()},
            {
              label: 'Dozens on Hand',
              value: dozensOnHand || null,
              sub: latestEggReport ? 'as of ' + fmt(latestEggReport.date) : '',
            },
          ]}
        />

        {/* Lifetime stats for active batches */}
        <div style={{display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap'}}>
          <h2 style={sectionTitleS}>Active Batches {'\u2014'} Lifetime</h2>
        </div>

        {/* Active batch cards */}
        <div style={{display: 'flex', flexDirection: 'column', gap: 14}}>
          {dashBatches.map(function (batch) {
            const myHousings = (layerHousings || []).filter((h) => h.batch_id === batch.id);
            const lifetimeFrom = lifetimeFromForBatch(batch);
            const cur = computeBatchWindow(batch, lifetimeFrom, today);
            const anchor = batch.brooder_entry_date || batch.arrival_date;
            // `.getTime()` is what the `-` operator already did: subtracting two
            // Dates coerces each through valueOf(), which returns exactly this
            // number.
            const ageMonths = anchor
              ? +(
                  (new Date(today + 'T12:00:00').getTime() - new Date(anchor + 'T12:00:00').getTime()) /
                  86400000 /
                  30.44
                ).toFixed(1)
              : 0;
            return (
              <div
                key={batch.id}
                {...openableProps(() => {
                  if (batch.id != null) navigate('/layer/batches/' + encodeURIComponent(batch.id));
                })}
                style={{...cardS, overflow: 'hidden', cursor: 'pointer'}}
                className="hoverable-tile"
              >
                <div
                  style={{
                    background: 'var(--surface)',
                    // R02: a divider inside the card is the hairline.
                    borderBottom: '1px solid var(--hairline)',
                    padding: '12px 20px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    flexWrap: 'wrap',
                  }}
                >
                  <span style={cardTitleS}>{batch.name}</span>
                  <Badge status="active" />
                  {ageMonths > 0 && <span style={cardMetaS}>{ageMonths} months old</span>}
                </div>
                <div style={{padding: '14px 20px'}}>
                  <MetricsGrid s={cur} />
                  {myHousings.map((housing) => {
                    const current = computeHousingWindow(housing, batch, lifetimeFrom, today);
                    const housingDot = layerGroupColor(groupPalette, housing.housing_name);
                    return (
                      <div
                        key={housing.id}
                        className="poultry-meta"
                        style={{marginTop: 6, display: 'flex', alignItems: 'center', gap: 6}}
                      >
                        {housingDot && <GroupDot color={housingDot} />}
                        <span>
                          {housing.housing_name} · {current.hens.toLocaleString()} hens
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>

        {/* Retirement Home card */}
        {retHome &&
          (() => {
            const cur = computeBatchWindow(retHome, retFrom, today);
            const retHomeDot = layerGroupColor(groupPalette, 'Retirement Home');
            return (
              <div style={{marginTop: 6}}>
                <div style={{display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10, flexWrap: 'wrap'}}>
                  <h2 style={sectionTitleS}>Retirement Home — Rolling Window</h2>
                  <PeriodToggle
                    val={retHomeDashPeriod}
                    setVal={setRetHomeDashPeriod}
                    opts={[
                      {v: 30, l: '30 Days'},
                      {v: 90, l: '90 Days'},
                      {v: 180, l: '6 Months'},
                    ]}
                  />
                </div>
                <div
                  {...openableProps(() => navigate('/layer/batches/' + retHome.id))}
                  style={{
                    ...cardS,
                    // R06: Retirement Home is a group; its card takes the group tint.
                    background: layerGroupTint(retHomeDot) ?? cardS.background,
                    padding: '18px 20px',
                    cursor: 'pointer',
                  }}
                  className="hoverable-tile"
                >
                  <div style={{display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12, flexWrap: 'wrap'}}>
                    {retHomeDot && <GroupDot color={retHomeDot} />}
                    <span style={cardTitleS}>Retirement Home</span>
                    <Badge status={retirementHomeStatus}>
                      {retirementHomeStatus
                        ? layerGroupStatusLabel(retirementHomeStatus)
                        : groupResource.loading
                          ? 'Loading status…'
                          : 'Status unavailable'}
                    </Badge>
                    <span style={cardMetaS}>{cur.hens.toLocaleString()} hens</span>
                  </div>
                  <MetricsGrid s={cur} showDaysDenominator={true} />
                </div>
              </div>
            );
          })()}
      </div>
    </div>
  );
}
